import { useEffect, useRef } from "react";
import UIkit from "uikit";
import { Image } from "uikit-react";
import classnames from "classnames";
import moment from "moment";
import Tags from "../../common/Tags";
import { getTaskTypeLabel } from "../taskTypeOptions";
import { getPriorityClassName, getPriorityLabel } from "../priorityOptions";

const safeUtc = (ts) => (ts ? moment.utc(ts) : null);

const hoursBetween = (fromUtc, toUtc) => {
  if (!fromUtc || !toUtc) return null;
  const diffMs = toUtc.diff(fromUtc);
  return diffMs / (1000 * 60 * 60);
};

const formatDuration = (hours) => {
  if (hours == null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const d = Math.floor(hours / 24);
  const h = Math.round(hours - d * 24);
  return `${d}d ${h}h`;
};

const formatDateTime = (ts) =>
  ts ? moment.utc(ts).local().format("MM/DD/YYYY h:mm a") : "—";

const chipKey = (value) => String(value || "other").toLowerCase().replace(/[^a-z0-9_-]+/g, "-");

const WorkOrderRow = ({
  workOrder,
  idx,
  rental,
  statusLabel,
  getAssignees,
  getOwner,
  diffDate,
  handleSelectedTags,
  handleEditWorkOrder,
  handleClickDelete,
  handleOpenGuestMessage,
  slug,
  showTags = true,
  showDelete = true,
  showEdit = true,
  user,
  userRecipientId,
  showTaskType = false,
  itemLabel = "Task",
  detailBasePath = "/work-orders",
  readOnly = false,
}) => {
  // --- Timing ---
  const now = moment.utc();
  const createdAt = safeUtc(workOrder.created_at);
  const startedAt = safeUtc(workOrder.started_at);
  const completedAt = safeUtc(workOrder.completed_at);
  const closedAt = safeUtc(workOrder.closed_at);

  // "Open for" means age until closed/completed; otherwise until now
  const endForOpenAge = closedAt || completedAt || now;
  const openHours = createdAt ? hoursBetween(createdAt, endForOpenAge) : null;

  // "Work time" (duration) means:
  // - If completed/closed: from started_at (or created_at fallback) to completed/closed
  // - If started but not completed: from started_at to now
  // - Else: null
  const endForDuration = closedAt || completedAt || now;
  const durationHours = completedAt || closedAt
    ? hoursBetween(startedAt || createdAt, endForDuration)
    : startedAt
      ? hoursBetween(startedAt, now)
      : null;

  // Determine state for icon row (backend can still use statusLabel)
  const state = (() => {
    const s = (workOrder.status || workOrder.state || statusLabel || "").toLowerCase();
    if (s.includes("closed") || !!closedAt) return "closed";
    if (s.includes("completed") || !!completedAt) return "completed";
    if (s.includes("started") || !!startedAt) return "started";
    return "not_started";
  })();

  // Owner display (from work_order_assignees with assignment_role='owner')
  const ownerName = getOwner ? getOwner(workOrder) : null;

  const assigneeNames = getAssignees(workOrder);
  const taskTypeLabel = getTaskTypeLabel(workOrder);
  const taskTypeKey = chipKey(workOrder?.task_type || workOrder?.type || taskTypeLabel);
  const hasGuestReservation = !!(
    workOrder?.guest_reservation?.guest_reservation_id ||
    workOrder?.guest_reservation_id
  );

  const tooltipLines = [
    `Created: ${formatDateTime(workOrder.created_at)}`,
    `Started: ${formatDateTime(workOrder.started_at)}`,
    `Completed: ${formatDateTime(workOrder.completed_at)}`,
    `Closed: ${formatDateTime(workOrder.closed_at)}`,
    `Open for: ${formatDuration(openHours)}`,
    state === "started" ? `In progress: ${formatDuration(durationHours)}` : `Duration: ${formatDuration(durationHours)}`,
  ].join("\n");

  const fmt = (dt) =>
    dt ? moment.utc(dt).local().format("MM/DD/YYYY hh:mm a") : "—";

  const fmtScheduled = () => {
    if (!workOrder.scheduled_date) return null;
    const d = moment(workOrder.scheduled_date);
    const st = workOrder.scheduled_start_time;
    const et = workOrder.scheduled_end_time;
    let label = d.format("MM/DD/YYYY");
    if (st) {
      const start = moment(st, "HH:mm:ss").format("h:mm a");
      label += ` ${start}`;
    }
    return label;
  };

  const ageLabel = () => {
    const created = workOrder.created_at ? moment.utc(workOrder.created_at) : null;
    const completed = workOrder.completed_at ? moment.utc(workOrder.completed_at) : null;

    if (!created) return "—";

    // If completed: show duration from created -> completed
    if (completed) {
      const d = moment.duration(completed.diff(created));
      const days = Math.floor(d.asDays());
      const hours = d.hours();
      const mins = d.minutes();

      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${mins}m`;
      return `${Math.max(1, mins)}m`;
    }

    const now = moment();
    const d = moment.duration(now.diff(created));
    const days = Math.floor(d.asDays());
    const hours = d.hours();
    const mins = d.minutes();

    if (days > 0) return `${days}d ${hours}h (open)`;
    if (hours > 0) return `${hours}h ${mins}m (open)`;
    return `${Math.max(1, mins)}m (open)`;
  };

  const ageFromLastUpdate = () => {
    if (!workOrder.updated_at) return "—";

    const updated = moment.utc(workOrder.updated_at);
    const now = moment();
    const d = moment.duration(now.diff(updated));

    const days = Math.floor(d.asDays());
    const hours = d.hours();
    const mins = d.minutes();

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${Math.max(1, mins)}m`;
  };

  const durationLabel = () => {
    const started = workOrder.started_at ? moment.utc(workOrder.started_at) : null;
    const completed = workOrder.completed_at ? moment.utc(workOrder.completed_at) : null;

    if (!started) return "—";

    const end = completed || moment();
    const d = moment.duration(end.diff(started));

    const days = Math.floor(d.asDays());
    const hours = d.hours();
    const mins = d.minutes();

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${Math.max(1, mins)}m`;
  };

  const durationTooltip = () => {
    if (!workOrder.started_at) return `${itemLabel} duration`;
    return workOrder.completed_at
      ? "Duration (started → completed)"
      : "Elapsed time since started";
  };

  const rowRef = useRef(null);

  useEffect(() => {
    if (!rowRef.current) return;
    UIkit.update(rowRef.current);
  }, [workOrder?.updated_at, workOrder?.created_at, workOrder?.started_at, workOrder?.completed_at]);

  return (
    <tr ref={rowRef} key={`workorder_${idx}`}>
      <td>
        <div>{workOrder.id}</div>
      </td>

      <td>
        <div>
          <div className="semi-bold-font">
            {workOrder.summary}
          </div>
          {workOrder.guest_reservation?.name && (
            <div className="secondary">Guest: {workOrder.guest_reservation.name}</div>
          )}

          {rental?.pms_id && (
            <div className="secondary">
              {rental.pms_id}
            </div>
          )}

          {rental?.address && <div className="secondary">{rental.address}</div>}
        </div>
      </td>

      <td>
        <div className="hms-wo-status-priority">
          <div className="uk-margin-small-bottom">
            {showTaskType && taskTypeLabel && (
              <div className={classnames("hms-chip task-type", `task-type-${taskTypeKey}`)}>
                {taskTypeLabel}
              </div>
            )}
            {statusLabel && (
              <div className="hms-booking-tag">
                {statusLabel.replace("-", " ")}
              </div>
            )}
            {workOrder.priority && (
              <div className={classnames("hms-chip", `priority-${getPriorityClassName(workOrder.priority)}`)}>
                {getPriorityLabel(workOrder.priority)}
              </div>
            )}
            {!showTaskType && workOrder.category && (
              <div className={classnames("hms-chip category capitalize")}>
                {workOrder.category}
              </div>
            )}
          </div>
        </div>

      </td>
      <td>
        <div className="wo-people">
          {assigneeNames && (
            <div
              className="wo-person uk-flex uk-flex-middle"
              uk-tooltip={`Assignee(s): ${assigneeNames}`}
            >
              <Image src="/images/tasker.svg" className="uk-margin-xsmall-right" />
              <span className="wo-person-text">{assigneeNames}</span>
            </div>
          )}
          {ownerName && (
            <div
              className="wo-person uk-flex uk-flex-middle"
              uk-tooltip={`Owner: ${ownerName}`}
            >
              <Image src="/images/property-manager.svg" className="uk-margin-xsmall-right" />
              <span className="wo-person-text">{ownerName}</span>
            </div>
          )}

          <div className={classnames({ "uk-margin-small-top": assigneeNames || ownerName })}>
            {fmtScheduled() && (
              <div className="uk-flex uk-flex-middle uk-margin-xsmall-bottom" uk-tooltip="Scheduled Start">
                <Image 
                  src="/images/age.svg" 
                  className="uk-margin-xsmall-right"
                />
                <span className="secondary">{fmtScheduled()}</span>
              </div>
            )}

            {workOrder.started_at && (
              <div className="uk-flex uk-flex-middle uk-margin-xsmall-bottom" uk-tooltip="Started At">
                <Image 
                  src="/images/started.svg" 
                  className="uk-margin-xsmall-right"
                />
                <span className="secondary">{fmt(workOrder.started_at)}</span>
              </div>
            )}

            {workOrder.completed_at && (
              <div className="uk-flex uk-flex-middle uk-margin-xsmall-bottom" uk-tooltip="Completed At">
                <Image 
                  src="/images/completed.svg" 
                  className="uk-margin-xsmall-right"
                />
                <span className="secondary">{fmt(workOrder.completed_at)}</span>
              </div>
            )}

            {workOrder.started_at && (
              <div
                className="uk-flex uk-flex-middle uk-margin-xsmall-bottom"
                uk-tooltip={durationTooltip()}
              >
                <Image
                  src="/images/duration.svg"
                  className="uk-margin-xsmall-right"
                  style={{opacity: 0.6}}
                />
                <span className="secondary">{durationLabel()}</span>
              </div>
            )}
          </div>
        </div>
      </td>

      <td>
        <div>
          {workOrder.checklist_item_count != null && workOrder.checklist_item_count > 0
            ? `${workOrder.checklist_item_count} item${
                workOrder.checklist_item_count !== 1 ? "s" : ""
              }`
            : ""}
        </div>
      </td>

      <td className="wo-cost-cell">
        {workOrder.total_cost != null && (
          <div>
            Tot: ${ (Number(workOrder.total_cost) / 100).toFixed(2) }
          </div>
        )}

        {(workOrder.material_cost || workOrder.labor_cost) && (
          <div className="secondary">
            {workOrder.material_cost && (
              <div>Mat: ${(Number(workOrder.material_cost) / 100).toFixed(2)}</div>
            )}
            {workOrder.labor_cost && (
              <div>Lab: ${(Number(workOrder.labor_cost) / 100).toFixed(2)}</div>
            )}
          </div>
        )}
      </td>

      {showTags && (
        <td className="hms-add-tag">
          <Tags
            value={workOrder?.tags || []}
            onChange={(event) => handleSelectedTags(event, workOrder)}
            readOnly={readOnly}
          />
        </td>
      )}
      <td>
        <span
          className="secondary"
          style={{ display: "inline-block" }}
          tabIndex={0}
          uk-tooltip={`title: Created: ${fmt(workOrder.created_at)}; pos: top-left`}
        >
          Updated: {fmt(workOrder.updated_at)}
        </span>

        <div
          className="uk-flex uk-flex-middle uk-margin-small-top"
          tabIndex={0}
          uk-tooltip="title: Time since last update; pos: top-left"
        >
          <Image src="/images/age.svg" className="uk-margin-xsmall-right" />
          <span className="secondary">{ageFromLastUpdate()}</span>
        </div>
      </td>
      <td className="hms-action-btn">
        <div className="uk-flex uk-flex-top">
          {(() => {
            // Resolve showEdit: true | false | "owner_only"
            const canEdit = showEdit === "owner_only"
              ? (workOrder.work_order_assignees || []).some(
                  (a) => a.assignment_role === "owner" && a.assignee_type === "recipient" && String(a.assignee_id) === String(userRecipientId)
                )
              : !!showEdit;

            return canEdit ? (
              <div className="uk-inline">
                <button
                  className="uk-button"
                  type="button"
                  onClick={() => handleEditWorkOrder(workOrder)}
                  uk-tooltip="Edit"
                >
                  <Image src="/images/edit.svg" />
                </button>
              </div>
            ) : null;
          })()}

          <div className="uk-inline">
            {slug ? (
              <a href={`/work-orders/${slug}/${workOrder.id}`} target="_blank" rel="noreferrer">
                <button className="uk-button" uk-tooltip="Comment">
                  <Image src="/images/workorder-comment.svg" />
                </button>
              </a>
            ) : (
              <a href={`${detailBasePath}/${workOrder.id}`} target="_blank" rel="noreferrer">
                <button className="uk-button" uk-tooltip="Comment">
                  <Image src="/images/workorder-comment.svg" />
                </button>
              </a>
            )}
          </div>

          {hasGuestReservation && (
            <div className="uk-inline">
              <button
                className="uk-button"
                type="button"
                uk-toggle="target: #modal-guest-messages"
                onClick={() => handleOpenGuestMessage(workOrder)}
                uk-tooltip="Send Text"
              >
                <Image src="/images/sms.svg" />
              </button>
            </div>
          )}

          {showDelete && (
            <div className="uk-inline">
              <button
                className="uk-button"
                uk-toggle="target: #work-order-delete"
                onClick={() => handleClickDelete(workOrder)}
                uk-tooltip="Delete"
              >
                <Image src="/images/delete.svg" />
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default WorkOrderRow;